/*
 * What the list says when it has no rows to show (design review "Loading and empty", P2): which kind
 * of empty it is, in the list's place, and the way on. Quiet, never decorated: an empty list is often
 * the rep's work done. Each way on is a quiet button with its total, as the rest of the empty states
 * offer theirs (`.lcr-list-empty-link`).
 * - A search or filter that matched nothing echoes the words, offers Inbox first, then
 *   each narrowing to take away.
 * - An empty label view names the label with its key, says what lands there, and offers Needs reply
 *   and Inbox with their totals.
 * - An empty Inbox while a campaign's history imports says so, with its progress, instead of "No
 *   replies yet"; so does one while the workspace's own mail still imports (LEA-483), which comes first.
 * - An empty Needs reply is the work done: a check, and the way to Inbox with its total (P3); with
 *   meeting requests still waiting, only the rest is done, and the way to them comes first.
 * - An empty Meeting requests says what lands there, and offers Needs reply and Inbox.
 * A list that failed to load says so in a block of its own, with the server's reference to quote.
 */
import { appCount } from "../../src/app-dates.ts";
import { type BackfillSummary, backfillCountText } from "../../src/backfill-progress.ts";
import type { ImportState } from "../../src/workspace-import.ts";
import { CheckIcon } from "../icons.tsx";
import type { MailChoice } from "../shell-nav.ts";
import { LabelDot } from "./label-key.tsx";
import { LABEL_NAMES, LABEL_VIEW_DETAILS, LABEL_VIEWS } from "./labels.ts";
import { coverageNote, type SearchCoverage } from "./search-coverage.ts";
import { campaignsDoneText } from "./workspace-import-note.tsx";

/** Where a search looks: the box on screen, and only it. */
function searchScope(choice: MailChoice, boxName: string): string {
  if (choice.kind === "box" && choice.box === "inbox")
    return "Search looks in Inbox. Snoozed and Done replies are searched from their own boxes.";
  return `Search looks in ${boxName} only.`;
}

/** What narrows a list, so an empty one can offer to take each away. */
type Narrowing = {
  filtered: boolean;
  /** Words typed in the search. */
  searched: boolean;
  /** Search operators or filters set. */
  narrowed: boolean;
  boxName: string;
};

/** What an empty list needs to say which empty it is, and to offer the way on. */
export type EmptyContext = {
  narrowing: Narrowing;
  choice: MailChoice;
  /** The words typed in the search, echoed by a search that matched nothing. */
  words: string;
  /** How much of the list the search index covers; null where no search is typed. */
  coverage: SearchCoverage | null;
  /** The totals an empty view offers the way to; undefined until the counts answer. */
  totals:
    | { meeting: number | undefined; needsReply: number | undefined; inbox: number }
    | undefined;
  /** The history import while one runs, for a person who may see it; null otherwise. */
  importing: BackfillSummary | null;
  /** The workspace's own import while it runs, for everyone; null once it is complete. */
  workspaceImporting: Exclude<ImportState, { state: "complete" }> | null;
  onRemoveFilters: () => void;
  onClearSearch: () => void;
  /** Searches the same words across Inbox; null where the list already is that. */
  onSearchInbox: (() => void) | null;
  onChoose: (choice: MailChoice) => void;
  /** Whose threads the rail's scope narrows to; null is everyone. */
  owner: string | null;
  /** The same place for everyone: the scope taken off. */
  onEveryone: () => void;
};

/** A quiet way on, with its total once the counts have it. */
function WayOn(props: { label: string; count?: number | undefined; onClick: () => void }) {
  return (
    <button type="button" className="lcr-list-empty-link" onClick={props.onClick}>
      {props.label}
      {/* Read as "Inbox 65"; the flex gap draws the space, so the space itself is never drawn. */}
      {props.count !== undefined && <span className="lcr-num"> {appCount(props.count)}</span>}
    </button>
  );
}

/** A search or a filter that matched nothing: the words echoed, then each way to widen it. */
function NothingMatches(props: { context: EmptyContext }) {
  const { narrowing, words } = props.context;
  return (
    <div className="lcr-list-empty" role="status">
      {/* Only a search that covers the whole list may say nothing matches. */}
      <p className="lcr-list-empty-heading">
        {coverageNote(props.context.coverage) === null
          ? words
            ? `Nothing matches “${words}”`
            : "Nothing matches"
          : words
            ? `Nothing found for “${words}”`
            : "Nothing found"}
      </p>
      {coverageNote(props.context.coverage) !== null && (
        <p className="lcr-list-empty-detail">{coverageNote(props.context.coverage)}</p>
      )}
      <p className="lcr-list-empty-detail">
        {searchScope(props.context.choice, narrowing.boxName)}
      </p>
      <div className="lcr-list-empty-links">
        {props.context.onSearchInbox && (
          <WayOn label="Search Inbox" onClick={props.context.onSearchInbox} />
        )}
        {narrowing.narrowed && (
          <WayOn label="Remove filters" onClick={props.context.onRemoveFilters} />
        )}
        {narrowing.searched && <WayOn label="Clear search" onClick={props.context.onClearSearch} />}
      </div>
    </div>
  );
}

/** An empty label view: the label named with its key, what lands there, and the way back. */
function NoneLabelled(props: { context: EmptyContext; label: MailChoice & { kind: "label" } }) {
  const { label } = props.label;
  const code = label === "none" ? null : label;
  const name =
    LABEL_VIEWS.find((view) => view.view === label)?.name ??
    (code === null ? "No label" : LABEL_NAMES[code]);
  const { totals, onChoose } = props.context;
  return (
    <div className="lcr-list-empty" role="status">
      <p className="lcr-list-empty-heading lcr-list-empty-label">
        <LabelDot code={code} />
        No replies in {name}
      </p>
      <p className="lcr-list-empty-detail">
        {code === null ? "Replies without a label land here." : LABEL_VIEW_DETAILS[code]}
      </p>
      <div className="lcr-list-empty-links">
        <WayOn
          label="Needs reply"
          count={totals?.needsReply}
          onClick={() => onChoose({ kind: "needs_reply" })}
        />
        <WayOn
          label="Inbox"
          count={totals?.inbox}
          onClick={() => onChoose({ kind: "box", box: "inbox" })}
        />
      </div>
    </div>
  );
}

/**
 * Needs reply with nothing in it: the work is done, said with a check, and the way to Inbox. While
 * meeting requests still wait, which Needs reply leaves to their own row (LEA-494), it is only the
 * rest that is done, and the way to them comes first.
 */
function AllAnswered(props: { context: EmptyContext }) {
  const { totals, onChoose } = props.context;
  const meetings = totals?.meeting ?? 0;
  return (
    <div className="lcr-list-empty" role="status">
      <p className="lcr-list-empty-heading lcr-list-empty-label">
        <CheckIcon size={16} />
        {meetings > 0 ? "Nothing else needs a reply" : "Nothing needs a reply"}
      </p>
      <p className="lcr-list-empty-detail">
        {meetings > 0
          ? "Every other lead's latest reply has an answer."
          : "Every lead's latest reply has an answer."}
      </p>
      <div className="lcr-list-empty-links">
        {meetings > 0 && (
          <WayOn
            label="Meeting requests"
            count={meetings}
            onClick={() => onChoose({ kind: "meeting" })}
          />
        )}
        <WayOn
          label="Inbox"
          count={totals?.inbox}
          onClick={() => onChoose({ kind: "box", box: "inbox" })}
        />
      </div>
    </div>
  );
}

/** Meeting requests with nothing in it (LEA-494): what lands there, and the ways on. */
function NoMeetingRequests(props: { context: EmptyContext }) {
  const { totals, onChoose } = props.context;
  return (
    <div className="lcr-list-empty" role="status">
      <p className="lcr-list-empty-heading">No meeting requests waiting</p>
      <p className="lcr-list-empty-detail">
        Interested replies that ask to meet, propose a time or ask how to book land here until you
        answer them.
      </p>
      <div className="lcr-list-empty-links">
        <WayOn
          label="Needs reply"
          count={totals?.needsReply}
          onClick={() => onChoose({ kind: "needs_reply" })}
        />
        <WayOn
          label="Inbox"
          count={totals?.inbox}
          onClick={() => onChoose({ kind: "box", box: "inbox" })}
        />
      </div>
    </div>
  );
}

/** Follow-up due with nothing in it (LEA-486): what lands there, and the way to Inbox. */
function NoFollowUps(props: { context: EmptyContext }) {
  const { totals, onChoose } = props.context;
  return (
    <div className="lcr-list-empty" role="status">
      <p className="lcr-list-empty-heading">No follow-ups due</p>
      <p className="lcr-list-empty-detail">
        Interested replies and questions you answered 3 to 10 working days ago, with no word back
        since, land here.
      </p>
      <div className="lcr-list-empty-links">
        <WayOn
          label="Inbox"
          count={totals?.inbox}
          onClick={() => onChoose({ kind: "box", box: "inbox" })}
        />
      </div>
    </div>
  );
}

/** What each box says empty: which kind of empty, and why it is so. */
const BOX_EMPTY = {
  done: ["Nothing is marked done", "Done replies return when the lead writes again."],
  snoozed: ["Nothing is snoozed", "Snoozed replies return on their date or when the lead writes."],
  sent: ["Nothing sent yet", "Conversations you have answered appear here."],
  trash: [
    "Trash is empty",
    "Deleted conversations stay here for 7 days, then LeadCue removes them. The mail stays in Bison.",
  ],
  inbox: ["No replies yet", "Replies to this workspace's campaigns appear here as they arrive."],
} as const;

/** Needs reply or Inbox empty while history imports: the import, not "No replies yet". */
function Importing(props: { summary: BackfillSummary }) {
  const { summary } = props;
  return (
    <div className="lcr-list-empty" role="status">
      <p className="lcr-list-empty-heading">History is importing</p>
      <p className="lcr-list-empty-detail">
        {summary.name ?? `${summary.campaigns} campaigns`}: {summary.percent}% done,{" "}
        {backfillCountText(summary)}. Replies appear here as they arrive.
      </p>
    </div>
  );
}

/** Needs reply or Inbox empty while the workspace's mail imports: not yet, rather than none. */
function WorkspaceImporting(props: { state: Exclude<ImportState, { state: "complete" }> }) {
  const { state } = props;
  return (
    <div className="lcr-list-empty" role="status">
      <p className="lcr-list-empty-heading">Replies are still importing</p>
      <p className="lcr-list-empty-detail">
        {state.state === "finding"
          ? "Finding this workspace's campaigns first."
          : `${campaignsDoneText(state.done, state.total)}.`}{" "}
        Replies appear here as they arrive.
      </p>
    </div>
  );
}

/** Whichever import leaves this list incomplete, the workspace's own first; null when none does. */
function importingNote(context: EmptyContext) {
  if (context.workspaceImporting) return <WorkspaceImporting state={context.workspaceImporting} />;
  return context.importing ? <Importing summary={context.importing} /> : null;
}

/**
 * Done, Snoozed, Sent or Trash with nothing in it, and the way back to Inbox: on a phone the rail is
 * folded away, so without it the empty box was a dead end (critique F6).
 */
function EmptyBox(props: { context: EmptyContext; box: "done" | "snoozed" | "sent" | "trash" }) {
  const { totals, onChoose } = props.context;
  const [heading, detail] = BOX_EMPTY[props.box];
  return (
    <div className="lcr-list-empty" role="status">
      <p className="lcr-list-empty-heading">{heading}</p>
      <p className="lcr-list-empty-detail">{detail}</p>
      <div className="lcr-list-empty-links">
        <WayOn
          label="Inbox"
          count={totals?.inbox}
          onClick={() => onChoose({ kind: "box", box: "inbox" })}
        />
      </div>
    </div>
  );
}

/** What Mine, Unassigned and one person's list say empty: assignment is a filter of the inbox. */
const ASSIGNED_EMPTY = {
  me: [
    "Nothing assigned to you",
    "Conversations someone assigns to you, or a routing rule sends you, appear here.",
  ],
  none: ["Every conversation has someone", "Replies no routing rule matches appear here."],
  person: ["Nothing assigned here", "Conversations assigned to this person appear here."],
} as const;

/** The same, on Needs reply or a label row, where only what waits on our side is listed. */
const ASSIGNED_WAITING = {
  me: "Nothing assigned to you needs a reply",
  none: "Every reply waiting has someone",
  person: "Nothing assigned here needs a reply",
} as const;

/**
 * A place narrowed to Mine, Unassigned or a colleague with nothing in it, and the same place for
 * everyone: an empty Mine never reads as an empty inbox.
 */
function EmptyAssigned(props: { context: EmptyContext; assigned: string }) {
  const key = props.assigned === "me" || props.assigned === "none" ? props.assigned : "person";
  const [plain, detail] = ASSIGNED_EMPTY[key];
  const { kind } = props.context.choice;
  const heading = kind === "needs_reply" || kind === "meeting" ? ASSIGNED_WAITING[key] : plain;
  return (
    <div className="lcr-list-empty" role="status">
      <p className="lcr-list-empty-heading">{heading}</p>
      <p className="lcr-list-empty-detail">{detail}</p>
      <div className="lcr-list-empty-links">
        <WayOn label="Everyone" onClick={props.context.onEveryone} />
      </div>
    </div>
  );
}

function Plain(props: { copy: readonly [string, string] }) {
  return (
    <div className="lcr-list-empty" role="status">
      <p className="lcr-list-empty-heading">{props.copy[0]}</p>
      <p className="lcr-list-empty-detail">{props.copy[1]}</p>
    </div>
  );
}

export function ListEmpty(props: { context: EmptyContext }) {
  const { context } = props;
  const { choice } = context;
  if (context.narrowing.filtered) return <NothingMatches context={context} />;
  if (context.owner) return <EmptyAssigned context={context} assigned={context.owner} />;
  if (choice.kind === "label") return <NoneLabelled context={context} label={choice} />;
  // While history imports the list is still filling: never "the work is done" over a partial list.
  if (choice.kind === "needs_reply")
    return importingNote(context) ?? <AllAnswered context={context} />;
  if (choice.kind === "meeting")
    return importingNote(context) ?? <NoMeetingRequests context={context} />;
  if (choice.kind === "follow_up")
    return importingNote(context) ?? <NoFollowUps context={context} />;
  if (choice.kind === "view")
    return <Plain copy={["No replies here", "Nothing in this view yet."]} />;
  if (choice.kind === "drafts")
    return (
      <Plain
        copy={[
          "No drafts",
          "Save a reply without sending it, and it waits here until you send or discard it.",
        ]}
      />
    );
  if (choice.box !== "inbox") return <EmptyBox context={context} box={choice.box} />;
  return importingNote(context) ?? <Plain copy={BOX_EMPTY.inbox} />;
}

/**
 * A list that failed to load, in the list's place: what failed, the server's reference to quote,
 * and Try again as a small button. Rows shown before stay under it, dimmed, so the rep keeps their
 * place (`useKeptList`).
 */
export function ListFailed(props: {
  reference: string | null;
  retrying: boolean;
  onRetry: () => void;
}) {
  return (
    <div className="lcr-list-empty lcr-list-failed" role="alert">
      <p className="lcr-list-empty-heading">Could not load conversations</p>
      {props.reference && (
        <p className="lcr-list-empty-detail">
          Reference <code className="lcr-list-failed-reference">{props.reference}</code>
        </p>
      )}
      <div className="lcr-list-empty-links">
        <button
          type="button"
          className="lcr-small-button"
          disabled={props.retrying}
          onClick={props.onRetry}
        >
          {props.retrying ? "Trying again…" : "Try again"}
        </button>
      </div>
    </div>
  );
}
